import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Lock, AlertTriangle, X, ShieldOff } from "lucide-react";
import PhoneInput from "@/components/PhoneInput";

export default function ProfileTab() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: user?.name || "",
    whatsapp: user?.whatsapp || "",
    email: user?.email || "",
  });
  const [emailPwdModal, setEmailPwdModal] = useState(false);
  const [emailPwd, setEmailPwd] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deletePwd, setDeletePwd] = useState("");
  const [deleting, setDeleting] = useState(false);

  const emailChanged = profile.email.trim().toLowerCase() !== (user?.email || "").toLowerCase();

  const saveProfile = async (currentPasswordForEmail) => {
    setSavingProfile(true);
    try {
      const payload = { name: profile.name.trim(), whatsapp: profile.whatsapp.trim() };
      if (emailChanged) { payload.email = profile.email.trim(); payload.current_password = currentPasswordForEmail; }
      const { data } = await api.patch("/account/profile", payload);
      setUser((prev) => ({ ...(prev || {}), name: data.name, email: data.email, whatsapp: data.whatsapp }));
      toast.success("Profile updated");
      setEmailPwdModal(false);
      setEmailPwd("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const onProfileSubmit = (e) => {
    e.preventDefault();
    if (emailChanged) { setEmailPwdModal(true); return; }
    saveProfile();
  };

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) { toast.error("New passwords don't match"); return; }
    if (pwd.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setSavingPwd(true);
    try {
      await api.post("/account/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password updated");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setSavingPwd(false);
    }
  };

  const onDelete = async () => {
    if (deleteConfirm !== "DELETE") { toast.error('Type "DELETE" to confirm'); return; }
    setDeleting(true);
    try {
      await api.post("/account/delete", { confirm: deleteConfirm, current_password: deletePwd });
      toast.success("Your account has been deactivated.");
      await logout();
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-tab">
      {/* Account Information */}
      <form onSubmit={onProfileSubmit} className="bg-white border border-[#e2e8f0] rounded-lg p-6 space-y-4" data-testid="profile-account-info">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-[#2563eb]" />
          <h2 className="font-display font-bold text-lg text-[#0f1b2d]">Account information</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name" required>
            <input
              type="text" required
              data-testid="profile-name"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="inp"
            />
          </Field>
          <Field label="WhatsApp / phone">
            <PhoneInput
              value={profile.whatsapp}
              onChange={(v) => setProfile({ ...profile, whatsapp: v })}
              testId="profile-whatsapp"
              optional
            />
          </Field>
        </div>
        <Field label="Email address" required hint={emailChanged ? "You'll be asked to confirm your current password." : ""}>
          <input
            type="email" required
            data-testid="profile-email"
            value={profile.email}
            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
            className="inp"
          />
        </Field>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={savingProfile}
            data-testid="profile-save-btn"
            className="bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold text-sm px-4 py-2 rounded-md transition"
          >
            {savingProfile ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>

      {/* Change Password */}
      <form onSubmit={onPasswordSubmit} className="bg-white border border-[#e2e8f0] rounded-lg p-6 space-y-4" data-testid="profile-change-password">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#2563eb]" />
          <h2 className="font-display font-bold text-lg text-[#0f1b2d]">Change password</h2>
        </div>
        <Field label="Current password" required>
          <input type="password" required data-testid="pwd-current" value={pwd.current_password}
            onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="inp" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="New password (min 8 chars)" required>
            <input type="password" required minLength={8} data-testid="pwd-new" value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="inp" />
          </Field>
          <Field label="Confirm new password" required>
            <input type="password" required minLength={8} data-testid="pwd-confirm" value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="inp" />
            {pwd.new_password && pwd.confirm && pwd.new_password !== pwd.confirm &&
              <div className="text-xs text-red-600 mt-1">Passwords don&apos;t match</div>}
          </Field>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={savingPwd} data-testid="pwd-submit-btn"
            className="bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold text-sm px-4 py-2 rounded-md transition">
            {savingPwd ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>

      {/* Danger Zone */}
      <div className="bg-white border border-red-200 rounded-lg p-6" data-testid="profile-danger-zone">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <h2 className="font-display font-bold text-lg text-red-700">Danger zone</h2>
        </div>
        <p className="mt-2 text-sm text-[#475569]">
          Deleting your account is permanent. Your order history will remain in our records for legal and business purposes, but you&apos;ll no longer be able to log in or place new orders.
        </p>
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setDeleteModal(true)}
            data-testid="delete-account-btn"
            className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white font-semibold text-sm px-4 py-2 rounded-md transition"
          >
            <ShieldOff className="w-4 h-4" /> Delete my account
          </button>
        </div>
      </div>

      {/* Email change password modal */}
      {emailPwdModal && (
        <Modal title="Confirm your password" onClose={() => { setEmailPwdModal(false); setEmailPwd(""); }} testId="email-pwd-modal">
          <p className="text-sm text-[#475569]">For security, please enter your current password to change your email address.</p>
          <form onSubmit={(e) => { e.preventDefault(); saveProfile(emailPwd); }} className="mt-4 space-y-3">
            <input
              type="password" required autoFocus data-testid="email-confirm-pwd"
              value={emailPwd} onChange={(e) => setEmailPwd(e.target.value)}
              placeholder="Current password" className="inp w-full"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setEmailPwdModal(false); setEmailPwd(""); }}
                className="text-sm font-semibold text-[#475569] hover:text-[#0f1b2d] px-3 py-2">Cancel</button>
              <button type="submit" disabled={savingProfile} data-testid="email-confirm-submit"
                className="bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold text-sm px-4 py-2 rounded-md">
                {savingProfile ? "Saving…" : "Confirm & save"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete confirm modal */}
      {deleteModal && (
        <Modal title="Delete my account" tone="danger" onClose={() => { setDeleteModal(false); setDeleteConfirm(""); setDeletePwd(""); }} testId="delete-modal">
          <p className="text-sm text-[#475569]">
            This action is permanent and cannot be undone. All your order history will remain in our records for legal/business purposes, but you will no longer be able to log in.
          </p>
          <div className="mt-4 space-y-3">
            <label className="block text-xs font-semibold text-[#0f1b2d]">Type <span className="font-mono bg-red-50 text-red-700 px-1.5 py-0.5 rounded">DELETE</span> to confirm</label>
            <input
              type="text" autoFocus data-testid="delete-confirm-input"
              value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE" className="inp w-full"
            />
            <label className="block text-xs font-semibold text-[#0f1b2d] pt-1">Current password</label>
            <input
              type="password" data-testid="delete-confirm-pwd"
              value={deletePwd} onChange={(e) => setDeletePwd(e.target.value)}
              placeholder="Your current password" className="inp w-full"
            />
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => { setDeleteModal(false); setDeleteConfirm(""); setDeletePwd(""); }}
              className="text-sm font-semibold text-[#475569] hover:text-[#0f1b2d] px-3 py-2">Cancel</button>
            <button onClick={onDelete} disabled={deleting || deleteConfirm !== "DELETE" || !deletePwd}
              data-testid="delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2 rounded-md">
              {deleting ? "Deleting…" : "Delete my account"}
            </button>
          </div>
        </Modal>
      )}

      <style>{`.inp { width: 100%; border: 1px solid #e2e8f0; background: #fff; color: #0f1b2d; border-radius: 6px; padding: 10px 12px; outline: none; font-size: 14px; }
        .inp:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); }`}</style>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#0f1b2d]">
        {label} {required && <span className="text-red-500">*</span>}
        {hint && <span className="ml-2 text-[10px] text-[#64748b] font-normal">{hint}</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Modal({ title, tone = "default", children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div data-testid={testId} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-md w-full shadow-xl">
        <div className={`flex items-center justify-between p-4 border-b ${tone === "danger" ? "border-red-200" : "border-[#e2e8f0]"}`}>
          <h3 className={`font-display font-bold ${tone === "danger" ? "text-red-700" : "text-[#0f1b2d]"}`}>{title}</h3>
          <button onClick={onClose} className="text-[#475569] hover:text-[#0f1b2d]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
